import { FC, Fragment, memo, forwardRef, useEffect } from 'react';
// i18next
import { useTranslation } from 'react-i18next';
// Hooks
import useToggle from 'hooks/useToggle';
// Components
import { Button } from 'components/Buttons';
import MediaDialog from 'dialogs/Media.dialog';

type Props = {
  label?: string;
  value: string;
  onChange: (audioUrl: string) => void;
  required?: boolean;
  disabled?: boolean;
  error?: boolean;
  errorText?: string;
  helperText?: string;
  playbackRate?: number;
  audioRef: React.RefObject<HTMLAudioElement>;
  startTime?: number;
  endTime?: number;
};

const AudioUpload: FC<Props> = forwardRef<any, Props>(({
  label = '', value, onChange, required = false, disabled = false,
  error = false, errorText = '', helperText = '', playbackRate = 1,
  audioRef, startTime = 0, endTime = 0,
  ...otherProps
}, ref) => {
  const { t } = useTranslation();

  const { open, toggle } = useToggle();

  const handleClickClear = () => {
    onChange('');
  };

  useEffect(() => {
    const audio = audioRef?.current;
    if (!audio) return;

    if (startTime !== undefined) {
      audio.currentTime = startTime;
    }

    if (playbackRate) {
      audio.playbackRate = playbackRate;
    }
  
    const handleTimeUpdate = () => {
      if (endTime !== undefined && endTime > 0 && audio.currentTime >= endTime) {
        audio.pause();
        audio.currentTime = startTime;
      }
    };
  
    audio.addEventListener('timeupdate', handleTimeUpdate);
  
    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
    };
  }, [audioRef, playbackRate, startTime, endTime]);

  return (
    <Fragment>
      {open ? (
        <MediaDialog open={open} onClose={toggle} type="audio" onChange={onChange} />
      ) : null}

      <div className="mb-4">
        <input ref={ref} type="hidden" name={(otherProps as any).name} value={value} />

        {label ? (
          <span className={`block text-white text-sm font-semibold mb-1 ${disabled ? 'opacity-50' : ''}`}>
            {t(label)}{required ? <Fragment>&nbsp;*</Fragment> : null}
          </span>
        ) : null}

        <div className="mb-2">
          {value ? (
            <audio ref={audioRef} controls className="w-full my-2">
              <source src={value} type="audio/mpeg" />
            </audio>
          ) : (
            <span className="text-white text-xs font-light">{t('labels.chooseAudio')}</span>
          )}
        </div>

        <div className="mb-1">
          <Button
            variant="outlined"
            disabled={disabled}
            onClick={toggle}
          >
            {t('buttons.browse')}
          </Button>
          <Button
            className="ml-2"
            variant="outlined"
            disabled={!value}
            onClick={handleClickClear}
          >
            {t('buttons.clear')}
          </Button>
        </div>

        {helperText ? (
          <span className="block text-gray-400 text-sm font-light leading-5 mt-2">{t(helperText)}</span>
        ) : null}
        {error && errorText ? (
          <span className="block text-red-500 text-sm py-1 px-4">{t(errorText)}</span>
        ) : null}
      </div>
    </Fragment>
  );
});

export default memo(AudioUpload, (prev, next) => {
  return prev.value === next.value && prev.error === next.error;
});
